import React from "react";
import { motion } from "framer-motion";
import SafeIcon from "../common/SafeIcon";
import * as FiIcons from "react-icons/fi";

const { FiHelpCircle } = FiIcons;

/**
 * FAQ Component
 * Standalone FAQ section with JSON-LD FAQPage schema
 */
export default function FAQ() {
  const faqs = [
    {
      q: "What is a green gas turbine?",
      a: "A green gas turbine is an advanced combustion turbine specifically designed to generate electricity and useful heat from low-carbon or zero-carbon fuels—primarily hydrogen (H\u2082), renewable natural gas (RNG), biogas, and ammonia. Unlike conventional turbines locked into fossil fuels, green gas turbines feature hydrogen-compatible combustion liners, fuel-flexible nozzles, and advanced digital controls that allow them to operate on any blend from 0% to 100% hydrogen while maintaining ultra-low NOx emissions (below 9 ppm with DLN burners). The term 'green' refers to the environmental profile of the fuel and the machine's ability to achieve near-zero CO\u2082 emissions at the point of combustion.",
    },
    {
      q: "How much hydrogen can green gas turbines burn?",
      a: "Modern green gas turbines can operate safely on up to 100% hydrogen by volume. The transition is typically staged: Phase 1 (0–30% H\u2082) requires only software tuning and minor burner adjustments; Phase 2 (30–60% H\u2082) involves upgraded combustors and fuel-system materials; Phase 3 (60–100% H\u2082) requires full combustor replacement with hydrogen-specific designs. Throughout all phases, dry-low-NOx (DLN) burners keep emissions below 9 ppm, and adaptive control algorithms adjust fuel-air ratios in real time to maintain flame stability and prevent flashback.",
    },
    {
      q: "What is the typical payback period for a green gas turbine?",
      a: "Industrial combined-heat-and-power (CHP) installations typically achieve an 18- to 30-month payback period. This rapid return is driven by $2–3 million in annual fuel savings per 50 MW installation, heat-recovery revenue from HRSG systems, avoided carbon-tax liabilities (which can exceed $8 million/year at EU ETS prices), and eligibility for clean-energy incentives such as the US IRA Section 45V hydrogen production credit. The exact payback depends on local fuel prices, carbon pricing, and the hydrogen blend ratio.",
    },
    {
      q: "Can existing legacy turbines be retrofitted for hydrogen?",
      a: "Yes. Retrofit kits are available for popular frame sizes including GE Frame 5/6, GE LM6000, Siemens SGT-600/800 series, and Mitsubishi H-25/H-100 models. A typical retrofit for 60% hydrogen blending on a 50 MW unit costs $8–12 million and can be completed during a scheduled major overhaul, minimising downtime. The retrofit includes upgraded combustion liners, hydrogen-resistant fuel piping (austenitic stainless steel or nickel alloys to prevent embrittlement), new fuel nozzles, and updated control software. This approach avoids stranding existing assets while providing a clear pathway to full decarbonisation.",
    },
    {
      q: "How do green gas turbines compare with battery energy storage?",
      a: "Green gas turbines and batteries are complementary technologies, not competitors. Lithium-ion batteries excel at short-duration storage (1–4 hours) and fast frequency response with 85–90% round-trip efficiency. However, for durations beyond 4–6 hours, battery costs scale linearly with capacity. A 100 MW / 8-hour battery system costs $400–600 million, while a 100 MW hydrogen-ready gas turbine with on-site fuel storage costs $80–120 million and can run indefinitely. For grid reliability during multi-day weather events (Dunkelflauten), green gas turbines provide the firm, dispatchable capacity that batteries cannot economically deliver.",
    },
    {
      q: "What fuels can green gas turbines use besides hydrogen?",
      a: "Green gas turbines are designed for multi-fuel flexibility. Beyond hydrogen, they can burn renewable natural gas (RNG) captured from landfills, dairy farms, or wastewater plants—chemically identical to fossil gas with 70–90% lifecycle CO\u2082 savings. Biogas (55–65% methane from anaerobic digestion) is another option after scrubbing. Some advanced models also support ammonia (NH\u2083) combustion, which contains no carbon atoms and offers high energy density for maritime and remote power applications. All units retain the ability to run on conventional natural gas, providing fuel-supply security during the transition.",
    },
    {
      q: "What are the NOx emissions from hydrogen combustion in gas turbines?",
      a: "Hydrogen combustion produces zero CO\u2082 but can generate thermal NOx due to higher flame temperatures (hydrogen burns at approximately 2,045°C versus 1,950°C for methane). Modern green gas turbines address this with dry-low-NOx (DLN) combustion technology that premixes fuel and air to create a lean, uniform flame below the thermal-NOx formation threshold of ~1,800 K. This achieves NOx levels below 9 ppm at 15% O\u2082—well within the strictest global emission standards including EU Industrial Emissions Directive limits and US EPA NSPS requirements—without requiring post-combustion SCR systems.",
    },
    {
      q: "How efficient are green gas turbines compared to conventional models?",
      a: "Green gas turbines match or exceed conventional turbine efficiency. In simple-cycle mode, they achieve 38–44% electrical efficiency. Combined-cycle configurations with heat-recovery steam generators (HRSG) reach 60–64%—the highest of any thermal generation technology. CHP installations that capture exhaust heat for industrial processes or district heating achieve total fuel utilisation above 90%. Hydrogen's higher flame temperature can slightly increase turbine inlet temperature, marginally boosting output, though operators must manage increased thermal stress on hot-section components.",
    },
    {
      q: "What is the cost of green hydrogen for turbine fuel?",
      a: "As of 2024, green hydrogen produced via renewable-powered electrolysis costs $3–6 per kilogram, depending on electricity prices and electrolyser utilisation. This is projected to fall below $2/kg by 2030 as electrolyser manufacturing scales and renewable electricity costs continue declining. At $2/kg, green hydrogen becomes cost-competitive with natural gas (on an energy-equivalent basis) in many markets, especially when carbon pricing is factored in. Government incentives—such as the US IRA's $3/kg Section 45V credit and the EU's hydrogen bank auctions—further accelerate cost parity.",
    },
    {
      q: "How long does it take to start a green gas turbine?",
      a: "Modern green gas turbines achieve full-load operation in under 10 minutes from a cold start—some aeroderivative models reach full power in as little as 5 minutes. This fast-start capability is critical for grid balancing, allowing turbines to ramp up quickly when wind or solar output drops unexpectedly. By comparison, coal plants require 4–8 hours for a cold start, and nuclear plants need 12–48 hours. This responsiveness makes green gas turbines ideal for peaking duty, ancillary services, and renewable firming applications.",
    },
    {
      q: "What maintenance do green gas turbines require?",
      a: "Green gas turbines follow similar maintenance schedules to conventional units: hot-gas-path inspections every 12,000–16,000 equivalent operating hours and major overhauls every 32,000–48,000 hours. Hydrogen operation may slightly accelerate hot-section wear due to higher flame temperatures, but advanced thermal barrier coatings (TBCs) and single-crystal turbine blades mitigate this. Digital twin technology and AI-driven predictive maintenance platforms monitor vibration, exhaust temperature spreads, and combustion dynamics in real time, enabling condition-based maintenance that reduces unplanned outages by up to 40%.",
    },
    {
      q: "Are green gas turbines eligible for clean-energy tax credits and incentives?",
      a: "Yes. Green gas turbine projects qualify for numerous incentives worldwide. In the United States, the Inflation Reduction Act (IRA) provides the Section 45V clean hydrogen production tax credit (up to $3/kg), the Section 48 investment tax credit for clean energy equipment, and accelerated depreciation (MACRS). The European Union offers funding through the Innovation Fund, REPowerEU hydrogen initiatives, and national programmes like Germany's H2Global. The UK's Net Zero Hydrogen Fund and Contracts for Difference (CfD) scheme also support hydrogen-to-power projects. These incentives can reduce effective project CapEx by 30–40%.",
    },
  ];

  // Generate FAQPage JSON-LD
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: a,
      },
    })),
  };

  return (
    <section id="faq" className="py-20 bg-gradient-to-br from-gray-50 to-white">
      {/* Inject schema for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-secondary-900 mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-secondary-600">
              Get answers to common questions about hydrogen-ready gas turbines and our solutions.
            </p>
          </motion.div>

          <div className="space-y-3">
            {faqs.map(({ q, a }, i) => (
              <motion.details
                key={q}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="group bg-white rounded-xl p-6 shadow-md hover:shadow-xl border border-gray-100 transition-all duration-300"
              >
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <span className="font-semibold text-lg text-secondary-900 pr-4">{q}</span>
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-50 flex items-center justify-center group-open:bg-primary-100 transition-colors">
                    <SafeIcon
                      icon={FiHelpCircle}
                      className="h-5 w-5 text-primary-600 transform group-open:rotate-180 transition-transform duration-300"
                    />
                  </div>
                </summary>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-secondary-700 leading-relaxed">{a}</p>
                </div>
              </motion.details>
            ))}
          </div>

          {/* Additional Help CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-12 text-center bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl p-8"
          >
            <h3 className="text-xl font-bold text-secondary-900 mb-3">
              Still have questions?
            </h3>
            <p className="text-secondary-700 mb-6 max-w-2xl mx-auto">
              Our team is ready to help. Reach out for personalized answers and technical guidance.
            </p>
            <a
              href="#quote"
              className="inline-block bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors shadow-md hover:shadow-lg"
            >
              Contact Our Team
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
